import { isEqualWith } from "lodash-es";

import { FlowVersion } from "src/api/flowTypes";
import {
  TemplateWithVersion,
  useAllCaseVersionsCombinedWithTemplates,
  useCaseTemplateDataSchema,
} from "src/cases/queries/templates";
import { CaseTemplateJsonSchemaResponse } from "src/clients/case-management/api";
import { CaseSchemaWithMetadata } from "src/schema/schemaEditor/CaseSchemaEditor";
import { transformTemplateToFlowSchema } from "src/schema/schemaEditor/caseSchemaUtils";
import { isCaseSchema } from "src/schema/schemaEditor/utils";

const DEFAULT_TEMPLATE_METADATA = {
  templateId: undefined,
  versionId: undefined,
  etag: undefined,
};

export const getTemplateMetadata = (
  inputSchema: FlowVersion["input_schema"],
): {
  templateId: string | undefined;
  versionId: string | undefined;
  etag?: string;
} => {
  if (!inputSchema || !isCaseSchema(inputSchema)) {
    return DEFAULT_TEMPLATE_METADATA;
  }

  const templateMetadata = (
    inputSchema.properties.case as CaseSchemaWithMetadata
  )._template_metadata;

  if (!templateMetadata) {
    return DEFAULT_TEMPLATE_METADATA;
  }

  return templateMetadata;
};

export type CaseTemplateInputSchemaError =
  | "archived"
  | "inconsistent"
  | "corrupted"
  | false;

export const useCaseTemplateInputSchemaError = (
  inputSchema: FlowVersion["input_schema"],
): CaseTemplateInputSchemaError => {
  const { templateId, versionId } = getTemplateMetadata(inputSchema);

  const versionsWithTemplates = useAllCaseVersionsCombinedWithTemplates({
    enabled: isCaseSchema(inputSchema),
  });

  const { data: templateSchemaResponse, isLoading: isLoadingSchema } =
    useCaseTemplateDataSchema(templateId, versionId, {
      enabled: isCaseSchema(inputSchema),
      throwOnError: false,
    });

  // If the templates or schema are still loading, we don't want to show an error
  if (versionsWithTemplates.isLoading || isLoadingSchema) return false;
  if (!inputSchema) return false;

  return isInvalidCaseTemplateInputSchema(
    inputSchema,
    versionsWithTemplates.data ?? [],
    templateSchemaResponse?.data,
  );
};

export const isInvalidCaseTemplateInputSchema = (
  inputSchema: Exclude<FlowVersion["input_schema"], null | undefined>,
  templatesData: TemplateWithVersion[],
  caseTemplateJsonSchema: CaseTemplateJsonSchemaResponse["data"] | undefined,
): CaseTemplateInputSchemaError => {
  if (!isCaseSchema(inputSchema)) return false;

  const currentCaseSchema = inputSchema.properties
    .case as CaseSchemaWithMetadata;

  const caseTemplateId = currentCaseSchema._template_metadata?.templateId;
  const caseTemplateVersionId = currentCaseSchema._template_metadata?.versionId;

  // If it is a case schema, but has no metadata, then something is wrong
  if (!caseTemplateId || !caseTemplateVersionId) return "corrupted";

  const correspondingTemplateVersion = templatesData.find(
    (template) =>
      template.versionId === caseTemplateVersionId &&
      template.templateId === caseTemplateId,
  );

  const caseJsonSchema = caseTemplateJsonSchema?.attributes?.case_json_schema;

  if (!correspondingTemplateVersion || !caseJsonSchema) return "archived";

  const schemaWithLatestCaseTemplateVersion = transformTemplateToFlowSchema(
    caseJsonSchema,
    {
      templateId: correspondingTemplateVersion.templateId,
      versionId: correspondingTemplateVersion.versionId,
      etag: correspondingTemplateVersion.version.attributes.etag,
      templateName:
        correspondingTemplateVersion.template.attributes.template_name,
      versionName: correspondingTemplateVersion.version.attributes.version_name,
    },
  );

  return !isEqualWith(
    schemaWithLatestCaseTemplateVersion,
    inputSchema,
    (_, __, key) => {
      if (key === "_template_metadata") {
        // We don't want to compare the template metadata,
        // because it doesn't affect input data validation
        return true;
      }
      return undefined;
    },
  )
    ? "inconsistent"
    : false;
};
